import { useState } from 'react';
import apiService from '../../services/api';
import logger from '../../utils/logging/logger';

export default function useSimplePasswordChangeModal({ onSuccess }) {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 10,
      digit: /\d/.test(password),
      specialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    };
    return requirements;
  };

  const passwordRequirements = validatePassword(formData.newPassword);
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
  const passwordsMatch = formData.newPassword === formData.confirmPassword && formData.confirmPassword !== '';
  const isDifferentFromCurrent = formData.newPassword !== formData.currentPassword && formData.newPassword !== '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!isPasswordValid) {
      setError('Please ensure your password meets all requirements');
      setLoading(false);
      return;
    }

    if (!passwordsMatch) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (!isDifferentFromCurrent) {
      setError('New password must be different from current password');
      setLoading(false);
      return;
    }

    try {
      const response = await apiService.changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (response.success) {
        setSuccess('Password changed successfully!');
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        setError(response.error || 'Failed to change password');
      }
    } catch (error) {
      logger.error('Password change error', { error });
      setError('Failed to change password. Please try again.');
    }

    setLoading(false);
  };

  return {
    formData,
    showPasswords,
    loading,
    error,
    success,
    passwordRequirements,
    isPasswordValid,
    passwordsMatch,
    isDifferentFromCurrent,
    handleChange,
    togglePasswordVisibility,
    handleSubmit
  };
}
